import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Filter, Search } from "lucide-react";
import {
  formatTeacherValue,
  getTeacherDisplayName,
  getTeacherEmail,
  getTeacherIdentifier,
  normalizeTeacherCollection,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  TeacherBanner,
  TeacherCard,
  TeacherEmptyState,
  TeacherLoadingState,
  TeacherPageShell,
} from "./TeacherUI";

export default function TeacherList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeParamKey = searchParams.get("year_id") ? "year_id" : "year";
  const activeYearValue = searchParams.get(activeParamKey) || "";
  const [filterValue, setFilterValue] = useState(activeYearValue);
  const [filterKey, setFilterKey] = useState(activeParamKey);

  const teacherListQuery = useQuery({
    queryKey: ["teacher-directory", activeParamKey, activeYearValue],
    queryFn: () =>
      teacherApi.getAllTeachers(
        activeYearValue ? { [activeParamKey]: activeYearValue } : {}
      ),
    retry: false,
  });

  const teachers = useMemo(
    () => normalizeTeacherCollection(teacherListQuery.data),
    [teacherListQuery.data]
  );

  const handleFilterSubmit = (event) => {
    event.preventDefault();
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("year");
    nextSearchParams.delete("year_id");

    if (filterValue.trim()) {
      nextSearchParams.set(filterKey, filterValue.trim());
    }

    setSearchParams(nextSearchParams);
  };

  return (
    <TeacherPageShell
      pageTitle="Teacher Directory"
      pageSubtitle="Public list powered by GET /api/get_all_teachers/."
      email="Public access"
      headerStatus="Public page"
      tabs={[]}
    >
      <TeacherBanner
        title="Browse Teachers"
        description="Optional year filtering is supported using either ?year=<id> or ?year_id=<id>."
      />

      <TeacherCard title="Directory Filter" description="Choose the query parameter key and year value to refetch the list.">
        <form className="teacher-toolbar" onSubmit={handleFilterSubmit}>
          <div className="teacher-field-group">
            <label htmlFor="teacher-list-filter-key">Query Key</label>
            <select
              id="teacher-list-filter-key"
              className="teacher-select"
              value={filterKey}
              onChange={(event) => setFilterKey(event.target.value)}
            >
              <option value="year">year</option>
              <option value="year_id">year_id</option>
            </select>
          </div>

          <div className="teacher-field-group">
            <label htmlFor="teacher-list-filter-value">Year Value</label>
            <input
              id="teacher-list-filter-value"
              className="teacher-input"
              type="text"
              value={filterValue}
              onChange={(event) => setFilterValue(event.target.value)}
              placeholder="Enter year id"
            />
          </div>

          <button className="teacher-button" type="submit">
            <Filter size={18} />
            Apply filter
          </button>

          <button
            className="teacher-button-secondary"
            type="button"
            onClick={() => {
              setFilterValue("");
              setSearchParams(new URLSearchParams());
            }}
          >
            Clear
          </button>
        </form>
      </TeacherCard>

      <div style={{ marginTop: "1.5rem" }}>
        <TeacherCard
          title="Teacher Results"
          description={`Showing ${teachers.length} teacher${teachers.length === 1 ? "" : "s"}.`}
        >
          {teacherListQuery.isLoading ? (
            <TeacherLoadingState label="Loading teachers..." />
          ) : teacherListQuery.isError ? (
            <TeacherEmptyState
              title="Unable to load teachers"
              description={teacherListQuery.error.message}
            />
          ) : !teachers.length ? (
            <TeacherEmptyState
              title="No teachers found"
              description="The backend returned an empty collection for the current filter."
            />
          ) : (
            <div className="teacher-list">
              {teachers.map((teacher, index) => {
                const teacherId = getTeacherIdentifier(teacher);
                const teacherName = getTeacherDisplayName(teacher);
                const teacherEmail = getTeacherEmail(teacher);
                const initials = teacherName
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((part) => part.charAt(0).toUpperCase())
                  .join("");

                return (
                  <div key={teacherId || `${teacherName}-${index}`} className="teacher-list-card">
                    <div className="teacher-list-card-main">
                      <div className="teacher-list-card-heading">
                        <div className="teacher-avatar">{initials || "T"}</div>
                        <div>
                          <h3>{teacherName}</h3>
                          <p>{teacherEmail || "Email not provided"}</p>
                        </div>
                      </div>

                      <div className="teacher-chip-row">
                        {teacher?.phone ? (
                          <span className="teacher-chip">
                            Phone: {formatTeacherValue(teacher.phone)}
                          </span>
                        ) : null}
                        {teacher?.year ? (
                          <span className="teacher-chip">
                            Year: {formatTeacherValue(teacher.year)}
                          </span>
                        ) : null}
                        {teacher?.experience ? (
                          <span className="teacher-chip">
                            Experience: {formatTeacherValue(teacher.experience)}
                          </span>
                        ) : null}
                        {teacher?.rating ? (
                          <span className="teacher-chip">
                            Rating: {formatTeacherValue(teacher.rating)}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="teacher-section-actions">
                      {teacherId ? (
                        <Link className="teacher-button" to={`/tutors/${teacherId}`}>
                          <Search size={18} />
                          View tutor
                        </Link>
                      ) : (
                        <span className="teacher-status-pill">Missing tutor id</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TeacherCard>
      </div>
    </TeacherPageShell>
  );
}
